import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    NgZone,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { BehaviorSubject, combineLatest, fromEvent, noop, Observable, of, Subject } from 'rxjs';
import { Store } from '@ngrx/store';

import { AuthService } from '@app/services/auth.service';
import * as fromApp from '@app/store/app.reducers';
import { getLoadingContact, getLoggedIn, getProfile } from '@app/store/app.reducers';
import { Profile } from '@app/services/profile.model';
import { Router } from '@angular/router';
import { distinctUntilChanged, finalize, map, takeUntil, mergeMap } from 'rxjs/operators';
import { MediaMatcher } from '@angular/cdk/layout';
import { cartColors, layout } from '@app-styles/styles-contants';
import { Platform } from '@angular/cdk/platform';
import { getLoggedInContact } from '@app/store/app.reducers';
import { Contact } from '@app/model/contact.model';
import { OrganisationConfig } from '@app/model/organisationConfig';
import { ContactService } from '@app/services/contact.service';
import { CrossBranchSharing } from '@app/model/cross-branch-sharing.model';

interface Menu {
    title: string;
    children?: Menu[];
    icon?: string;
    iconRef?: TemplateRef<void>;
    iconStyle?: string;
    id?: string;
    command(): any;
}

@Component({
    selector: 'app-profile-menu-container',
    templateUrl: './profile-menu-container.component.html',
    styleUrls: ['./profile-menu-container.component.scss']
})
export class ProfileMenuContainerComponent implements OnInit, OnDestroy, AfterViewInit {

    private destroy$ = new Subject<void>();

    @ViewChild('badge', { static: false }) badgeTpl: TemplateRef<void>;

    menu$: Observable<Menu[]>;
    isSmall$ = new BehaviorSubject(false);
    matcher: MediaQueryList;
    badgeStyle: any;

    onLogin() {
        localStorage.setItem('auth-entry-url', this.router.url);
        this.router.navigate(['/account/login']);
    }

    onCreateAccount() {
        this.router.navigate(['account', 'create']);
    }

    watchMatchMedia(): void {
        const { desktop } = layout;

        if (desktop) {
            const match = this.mediaMatcher.matchMedia(`${desktop}`).matches;
            this.isSmall$.next(!match);
            this.ngZone.run(() => {
                this.cdr.markForCheck();
            });
        }
    }

    getMenu$() {
        return combineLatest([
            this.store.select(getLoggedIn),
            this.store.select(getProfile),
            this.store.select(getLoadingContact),
            this.store.select(getLoggedInContact),
            this.isSmall$.pipe(distinctUntilChanged()),
            this.store.select(getLoggedIn).pipe(
                mergeMap(isLoggedin => this.getOrganisationConfig$(isLoggedin))
            )
        ]).pipe(
            map(([isLoggedIn, profile, isLoadingContact, loggedInContact, isSmall, organisationConfig]) => {
                const cartMenu = [{ title: 'Cart', iconRef: this.badgeTpl, command: () => this.router.navigate(['/checkout']) }];

                if (isSmall) {
                    if (organisationConfig.canOrderLabTests || organisationConfig.canOrderSamplingSupplies) {
                        return cartMenu;
                    } else {
                        return [];
                    }
                }

                const notLoggedInMenu = [
                    {
                        title: 'Register', icon: 'icons/fontawesome/6.5.1/thin:pencil',
                        command: () => this.onCreateAccount(),
                        id: 'header-register-button'
                    },
                    {
                        title: 'Log In', icon: 'icons/fontawesome/6.5.1/thin:user',
                        command: () => this.onLogin(),
                        id: 'header-login-button'
                    }
                ];

                const profileMenu = !isLoggedIn
                    ? notLoggedInMenu
                    : this.getLoggedInMenu(profile, isLoadingContact, loggedInContact, isSmall);

                if (organisationConfig.canOrderLabTests || organisationConfig.canOrderSamplingSupplies) {
                    return [
                        ...profileMenu,
                        ...cartMenu
                    ];
                } else {
                    return profileMenu;
                }
            })
        );
    }

    private getOrganisationConfig$(loggedin: boolean): Observable<OrganisationConfig> {
        if (!loggedin) {
            return of(new OrganisationConfig());
        } else {
            return this.contactService.getOrganisationConfig(AuthService.getAccessToken());
        }
    }

    constructor(
        private store: Store<fromApp.AppState>,
        private auth: AuthService,
        private router: Router,
        private mediaMatcher: MediaMatcher,
        private ngZone: NgZone,
        private platform: Platform,
        private cdr: ChangeDetectorRef,
        private contactService: ContactService
    ) {
    }

    ngOnInit() {

        this.menu$ = this.getMenu$();
        this.initUI();

    }

    ngAfterViewInit(): void {
        if (this.platform.isBrowser) {
            Promise.resolve().then(() => this.watchMatchMedia());
            const resizeEvent$ = fromEvent(window, 'resize');
            resizeEvent$
                .pipe(
                    takeUntil(this.destroy$)
                )
                .subscribe(() => this.watchMatchMedia());
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private getLoggedInMenu(profile, isLoadingContact: boolean, loggedInContact: Contact, isSmallScreen): Menu[] {

        let menu = [];

        if (isSmallScreen) {
            menu = [{ title: 'My Account', icon: 'icons/fontawesome/6.5.1/thin:user', command: () => this.router.navigate(['profile']) }];
        } else if (!!profile && !!profile.name && !isLoadingContact) {
            menu = [
                {
                    title: 'Hello ' + this.getProfileName(profile, loggedInContact)
                },
                {
                    title: 'My Account',
                    icon: 'icons/fontawesome/6.5.1/thin:user',
                    command: () => noop(),
                    id: 'header-account-button',
                    children: [
                        {
                            title: 'My Account',
                            icon: 'fa fa-user',
                            command: () => this.router.navigate(['profile']),
                            id: 'header-profile-button'
                        },
                        {
                            title: 'Logout',
                            icon: 'fa fa-sign-out',
                            command: () => this.auth.logout(),
                            id: 'header-logout-button' }
                    ]
                }
            ];
        }

        return menu;
    }

    private initUI() {
        const { badgeBg } = cartColors;
        this.badgeStyle = {
            backgroundColor: `${badgeBg}`,
            height: '1.1rem',
            minWidth: '1.1rem',
            fontSize: '0.6rem',
            lineHeight: '1.1rem',
            borderRadius: '0.55rem',
            padding: 0,
            boxShadow: 'none'
        };
    }

    private getProfileName(profile: Profile, loggedInContact: Contact) {
        let profileName = profile.name;

        if (!!loggedInContact) {
            profileName = (loggedInContact.firstName + ' ' + loggedInContact.lastName);
        }

        return profileName;
    }
}
